import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Modal, Spin } from 'antd';
import {
  usePassport,
  usePassportArrivals,
  usePassportTransactions,
} from '@/api/Passport';
import { useRepresentativeImage } from '@/api/ProductImages/useRepresentativeImage';
import { PassportsTransactionData } from '@/types';
import { timeConverter } from '@/utils/helpers';
import { GbaLogo } from '@/ui/components/Logo/GbaLogo';
import { ArrivalStamp } from './ArrivalStamp';
import { TransactionLedger } from './TransactionLedger';
import { LocationEvidence } from './LocationEvidence';
import './PassportBook.scss';
import './TravelRecord.scss';

// Concept 2: a vertical trail of arrival stamps beside the selected visit sheet,
// on OneView's white workspace. Same data and rules as the Concept 1 book.

const day = (value?: string | null) =>
  timeConverter(value || undefined, 'DD MMM YYYY');
const dayTime = (value?: string | null) =>
  timeConverter(value || undefined, 'DD MMM YYYY, HH:mm');
const pad = (n: number) => String(n).padStart(2, '0');

export const TravelRecord: FC<{ vin: string }> = ({ vin }) => {
  const enabled = !!vin;
  const passportQuery = usePassport(vin, { enabled });
  const transactionsQuery = usePassportTransactions(vin, { enabled });
  const arrivalsQuery = usePassportArrivals(vin, { enabled });
  const passport = passportQuery.data;
  const { image } = useRepresentativeImage(
    passport?.vehicleId,
    !passport?.images?.length,
  );

  const [selectedId, setSelectedId] = useState<number>();
  // 'all' shows the complete record; otherwise the selected visit's sheet.
  const [scope, setScope] = useState<'visit' | 'all'>('visit');
  const [showLedger, setShowLedger] = useState(false);
  const [evidence, setEvidence] = useState<PassportsTransactionData>();

  useEffect(() => setEvidence(undefined), [selectedId, scope, showLedger]);

  const arrivals = useMemo(
    () =>
      [...(arrivalsQuery.data || [])].sort((a, b) =>
        a.arrivedAt.localeCompare(b.arrivedAt),
      ),
    [arrivalsQuery.data],
  );
  const transactions = useMemo(
    () => transactionsQuery.data || [],
    [transactionsQuery.data],
  );

  if (!enabled)
    return (
      <Empty className="mt-5" description="Search a VIN to open its passport" />
    );
  if (passportQuery.isLoading) return <Spin className="d-block mt-5" />;
  if (passportQuery.isError)
    return (
      <Alert
        type="error"
        showIcon
        message="The passport could not be loaded. Please try again."
      />
    );
  if (!passport?.vehicleId)
    return <Empty className="mt-5" description="No vehicle matches this VIN" />;

  const selected =
    arrivals.find((a) => a.arrivalId === selectedId) ?? arrivals[0];
  const first = arrivals[0];
  const last = arrivals[arrivals.length - 1];
  const photo = passport.images?.[0];
  // Same visit window as Concept 1: arrival to departure, or to the next arrival.
  const hasVisitLink = transactions.some((t) => t.arrivalId != null);
  const visitEnd =
    selected &&
    (selected.departedAt ??
      arrivals[arrivals.indexOf(selected) + 1]?.arrivedAt);
  const visitTransactions = selected
    ? transactions.filter((t) => {
        if (hasVisitLink) return t.arrivalId === selected.arrivalId;
        const at = new Date(t.transDate).getTime();
        return (
          at >= new Date(selected.arrivedAt).getTime() &&
          (!visitEnd || at <= new Date(visitEnd).getTime())
        );
      })
    : [];
  const recent = [...visitTransactions]
    .sort((a, b) => (b.transDate || '').localeCompare(a.transDate || ''))
    .slice(0, 3);
  const showAll = scope === 'all' || !selected;
  // Shown instead of transaction data, so a failed request never reads as an
  // empty audit record.
  const transactionsStatus = transactionsQuery.isLoading ? (
    <Spin />
  ) : transactionsQuery.isError ? (
    <Alert type="error" showIcon message="Transactions could not be loaded." />
  ) : null;

  return (
    <>
      <section className="tr-card tr-identity">
        <img
          className="tr-thumb"
          src={
            photo?.resizedImageUrl ||
            photo?.url ||
            image?.url ||
            '/images/reference-vehicle.jpg'
          }
          alt={`${passport.manufacturer} ${passport.model}`}
        />
        <div>
          <span className="tr-eyebrow">Vehicle identity</span>
          <h2 className="tr-model">
            {[passport.manufacturer, passport.model].filter(Boolean).join(' ')}
          </h2>
          <span className="tr-vin">VIN {passport.vin}</span>
        </div>
        <div className="tr-arrivals">
          <span className="tr-label">First recorded arrival</span>
          <strong>{first ? day(first.arrivedAt) : 'Not recorded'}</strong>
          <span className="tr-label mt-2">Last recorded arrival</span>
          <strong>
            {last
              ? `${last.siteName} · ${day(last.arrivedAt)}`
              : arrivalsQuery.isError
                ? 'Arrival history unavailable'
                : 'Not recorded'}
          </strong>
        </div>
        <div className="tr-flag" aria-hidden="true">
          <GbaLogo fill="currentColor" width="130" height="112" />
          <span>Historical record</span>
        </div>
      </section>

      <div className="tr-body">
        <section className="tr-card tr-trail">
          <h2 className="tr-title">Site arrival trail</h2>
          <p className="tr-sub">Recorded visits, oldest first</p>
          {arrivalsQuery.isLoading ? (
            <Spin />
          ) : arrivalsQuery.isError ? (
            <p className="pb-note">Arrival history is unavailable.</p>
          ) : !arrivals.length ? (
            <p className="pb-note">
              No arrivals have been recorded for this vehicle.
            </p>
          ) : (
            <ol>
              {arrivals.map((a, index) => {
                const isOpen = scope === 'visit' && a === selected;
                const open = () => {
                  setSelectedId(a.arrivalId);
                  setScope('visit');
                  setShowLedger(false);
                };
                return (
                  <li key={a.arrivalId} className={isOpen ? 'is-open' : ''}>
                    <ArrivalStamp
                      arrival={a}
                      index={index}
                      selected={isOpen}
                      onSelect={open}
                      tilt={false}
                    />
                    <div className="tr-stop">
                      <span className="tr-eyebrow">
                        Visit {pad(a.visitNumber || index + 1)}
                      </span>
                      <strong>{a.siteName}</strong>
                      <button type="button" className="tr-link" onClick={open}>
                        View record →
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          <button
            type="button"
            className={`tr-all${showAll ? ' is-open' : ''}`}
            onClick={() => setScope('all')}
          >
            <span className="tr-eyebrow">Complete record</span>
            <strong>All transactions →</strong>
          </button>
        </section>

        <section className="tr-card tr-sheet">
          {showAll ? (
            <>
              <span className="tr-eyebrow">Complete record</span>
              <h2 className="tr-site">All transactions</h2>
              <p className="tr-meta">
                Every recorded event, including those without a confirmed site
                visit
              </p>
              <hr />
              {transactionsStatus ?? (
                <TransactionLedger
                  title="Transaction ledger"
                  transactions={transactions}
                  total={transactions.length}
                  onShowPosition={setEvidence}
                />
              )}
            </>
          ) : (
            <>
              <span className="tr-corner">
                GBA • {pad(selected.visitNumber)}
              </span>
              <span className="tr-eyebrow">
                Visit sheet {pad(selected.visitNumber)}
              </span>
              <h2 className="tr-site">{selected.siteName}</h2>
              <p className="tr-meta">
                <span>Arrived {dayTime(selected.arrivedAt)}</span>
                <span>
                  {selected.departedAt
                    ? `Departure recorded ${dayTime(selected.departedAt)}`
                    : 'Departure not recorded'}
                </span>
              </p>
              <hr />
              {showLedger ? (
                <>
                  {transactionsStatus ?? (
                    <TransactionLedger
                      key={selected.arrivalId}
                      compact
                      title="Transaction ledger"
                      transactions={visitTransactions}
                      total={visitTransactions.length}
                      onShowPosition={setEvidence}
                    />
                  )}
                  <button
                    type="button"
                    className="tr-link mt-2"
                    onClick={() => setShowLedger(false)}
                  >
                    ← Visit overview
                  </button>
                </>
              ) : (
                <>
                  <span className="tr-label">Visit overview</span>
                  <dl className="tr-overview">
                    <dt>Recorded site</dt>
                    <dd>{selected.siteName}</dd>
                    <dt>Arrival stamp</dt>
                    <dd>{dayTime(selected.arrivedAt)?.replace(',', ' ·')}</dd>
                    <dt>Departure record</dt>
                    <dd>
                      {selected.departedAt
                        ? dayTime(selected.departedAt)?.replace(',', ' ·')
                        : 'Departure not recorded'}
                    </dd>
                    <dt>Transactions</dt>
                    <dd>
                      {transactionsStatus
                        ? '—'
                        : `${visitTransactions.length} recorded events`}
                    </dd>
                  </dl>
                  <div className="tr-recent">
                    <span className="tr-label">Recent transactions</span>
                    {transactionsStatus ??
                      (recent.length ? (
                        <table>
                          <tbody>
                            {recent.map((t) => (
                              <tr key={t.vehicleTransIndex}>
                                <td>
                                  {timeConverter(t.transDate, 'DD MMM HH:mm')}
                                </td>
                                <td>{t.transDescription}</td>
                                <td>{t.transType}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="tr-sub mb-0">
                          No transactions recorded during this visit.
                        </p>
                      ))}
                  </div>
                  {!transactionsStatus && (
                    <button
                      type="button"
                      className="tr-link tr-view-all"
                      onClick={() => setShowLedger(true)}
                    >
                      View all {visitTransactions.length} →
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </section>
      </div>

      <Modal
        open={!!evidence}
        onCancel={() => setEvidence(undefined)}
        footer={null}
        closable={false}
        centered
        width={760}
        className="tr-evidence"
        styles={{ content: { padding: 0, overflow: 'hidden' } }}
        destroyOnHidden
      >
        {evidence && (
          <LocationEvidence
            transaction={evidence}
            onClose={() => setEvidence(undefined)}
          />
        )}
      </Modal>
    </>
  );
};
